import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../../auth/AuthContext'
import InventoryPicker from '../InventoryPicker'
import { ioGet, ioSend, ioSendForm } from './api'
import { btnPrimary, btnSecondary, cardClass, inputClass } from './ui'

const emptyLine = () => ({
  id: `li-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
  type: 'screens',
  name: 'Screen selection',
  displayUnitIds: [],
  packageId: null,
  packageName: '',
  flight: { startDate: '', endDate: '', durationDays: 7, adDurationSec: 15, saturation: '1 per loop' },
})

export default function CampaignBuilder() {
  const { user } = useAuth()
  const navigate = useNavigate()
  const [advertisers, setAdvertisers] = useState([])
  const [packages, setPackages] = useState([])
  const [criteria, setCriteria] = useState([])
  const [name, setName] = useState('')
  const [advertiserName, setAdvertiserName] = useState(user?.companyName || '')
  const [contactName, setContactName] = useState(user?.contactPerson || user?.name || '')
  const [email, setEmail] = useState(user?.contactEmail || user?.mail || '')
  const [phone, setPhone] = useState(user?.contactPhone || '')
  const [goalName, setGoalName] = useState('')
  const [goalType, setGoalType] = useState('impressions')
  const [goalTarget, setGoalTarget] = useState('')
  const [exclusionIds, setExclusionIds] = useState([])
  const [lineItems, setLineItems] = useState([])
  const [pickingFor, setPickingFor] = useState(null)
  const [files, setFiles] = useState([])
  const [description, setDescription] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [pkgPick, setPkgPick] = useState('')

  useEffect(() => {
    ioGet('/api/io/advertisers').then((d) => setAdvertisers(d.advertisers || [])).catch(() => {})
    ioGet('/api/io/packages').then((d) => setPackages(d.packages || [])).catch(() => {})
    // Scope to the user's currently-selected venue so an unmigrated venue (no display
    // units in Control yet) correctly gets back an empty Criteria list instead of
    // Control's full, venue-independent list -- see /api/io/inventory's venueUid gate.
    // (2026-09-17, per Matt.)
    const criteriaUrl = user?.venueUid
      ? `/api/io/inventory?venueUid=${encodeURIComponent(user.venueUid)}`
      : '/api/io/inventory'
    ioGet(criteriaUrl).then((d) => setCriteria(d.criteria || [])).catch(() => {})
  }, [user?.venueUid])

  const addScreens = () => {
    const item = emptyLine()
    setLineItems((prev) => [...prev, item])
    setPickingFor(item.id)
  }

  const addPackage = () => {
    const pkg = packages.find((p) => p.id === pkgPick)
    if (!pkg) return
    const ids = Array.isArray(pkg.display_unit_ids) ? pkg.display_unit_ids.map(Number) : []
    const flight = pkg.flight_defaults || {}
    setLineItems((prev) => [...prev, {
      ...emptyLine(),
      type: 'package',
      name: pkg.name,
      packageId: pkg.id,
      packageName: pkg.name,
      displayUnitIds: ids,
      priceCents: pkg.price_cents,
      flight: {
        startDate: '',
        endDate: '',
        durationDays: flight.durationDays || 7,
        adDurationSec: flight.adDurationSec || 15,
        saturation: flight.saturation || '1 per loop',
        daysOfWeek: flight.daysOfWeek,
        startTime: flight.startTime,
        endTime: flight.endTime,
      },
    }])
    setPkgPick('')
  }

  const updateLine = (id, patch) => {
    setLineItems((prev) => prev.map((li) => li.id === id ? { ...li, ...patch } : li))
  }

  const save = async (ioAction) => {
    if (!name.trim()) {
      setError('Campaign name is required')
      return
    }
    setBusy(true)
    setError('')
    const startDate = lineItems.map((l) => l.flight?.startDate).filter(Boolean).sort()[0] || ''
    const endDate = lineItems.map((l) => l.flight?.endDate).filter(Boolean).sort().slice(-1)[0] || ''
    const sharedGoals = goalTarget
      ? [{ name: goalName || 'Campaign goal', type: goalType, target: Number(goalTarget) }]
      : []
    const payload = {
      recordKind: 'guaranteed',
      ioAction,
      campaignName: name.trim(),
      advertiserName: advertiserName.trim() || 'Advertiser',
      contactEmail: email,
      contactPhone: phone,
      startDate: startDate || undefined,
      endDate: endDate || undefined,
      description,
      lineItems,
      sharedGoals,
      selectedCriteria: exclusionIds.length ? { not: exclusionIds } : null,
      impressionGoal: goalType === 'impressions' ? Number(goalTarget) || undefined : undefined,
      durationMsec: 15000,
      formData: { contactName, exclusionIds },
    }
    try {
      if (files.length && ioAction === 'submit') {
        const fd = new FormData()
        fd.append('advertiserName', payload.advertiserName)
        fd.append('contactEmail', email)
        fd.append('contactPhone', phone)
        fd.append('campaignName', payload.campaignName)
        fd.append('adType', 'guaranteed')
        if (startDate) fd.append('startDate', startDate)
        if (endDate) fd.append('endDate', endDate)
        fd.append('specialInstructions', description)
        fd.append('recordKind', 'guaranteed')
        fd.append('ioAction', ioAction)
        fd.append('formDataJson', JSON.stringify({
          ...payload.formData,
          lineItems,
          sharedGoals,
          selectedDisplayUnitIds: lineItems.flatMap((l) => l.displayUnitIds || []),
          selectedCriteria: payload.selectedCriteria,
          impressionGoal: payload.impressionGoal,
          durationMsec: 15000,
          ioAction,
        }))
        files.forEach((f) => fd.append('files', f))
        if (files[0]) {
          fd.append('fileMetadata', JSON.stringify({
            0: { alt: name.trim() || 'Campaign creative' },
            [files[0].name]: { alt: name.trim() || 'Campaign creative' },
          }))
        }
        const result = await ioSendForm('/api/orders', fd)
        navigate(`/campaigns/${result.id}`)
      } else {
        const result = await ioSend('/api/io/campaigns', 'POST', payload)
        navigate(`/campaigns/${result.id}`)
      }
    } catch (e) {
      setError(e.message)
    } finally {
      setBusy(false)
    }
  }

  const picking = lineItems.find((l) => l.id === pickingFor)

  return (
    <div className="max-w-5xl mx-auto space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">Campaign builder</h1>
        <p className="text-sm text-gray-500 mt-1">Name the campaign, add line items from packages or screens, then hold or submit for booking.</p>
      </div>

      <section className={`${cardClass} p-6 space-y-3`}>
        <h2 className="font-medium text-gray-900 dark:text-white">Campaign</h2>
        <input className={inputClass} placeholder="Campaign name *" value={name} onChange={(e) => setName(e.target.value)} />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <input className={inputClass} list="advertiser-list" placeholder="Advertiser name" value={advertiserName}
            onChange={(e) => setAdvertiserName(e.target.value)} />
          <datalist id="advertiser-list">
            {advertisers.map((a) => <option key={a} value={a} />)}
          </datalist>
          <input className={inputClass} placeholder="Contact name" value={contactName} onChange={(e) => setContactName(e.target.value)} />
          <input className={inputClass} placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} />
          <input className={inputClass} placeholder="Phone" value={phone} onChange={(e) => setPhone(e.target.value)} />
        </div>
      </section>

      <section className={`${cardClass} p-6 space-y-3`}>
        <h2 className="font-medium text-gray-900 dark:text-white">Shared goal (optional)</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <input className={inputClass} placeholder="Goal name" value={goalName} onChange={(e) => setGoalName(e.target.value)} />
          <select className={inputClass} value={goalType} onChange={(e) => setGoalType(e.target.value)}>
            <option value="impressions">Impressions</option>
            <option value="repetitions">Repetitions</option>
          </select>
          <input className={inputClass} type="number" placeholder="Target" value={goalTarget} onChange={(e) => setGoalTarget(e.target.value)} />
        </div>
      </section>

      <section className={`${cardClass} p-6 space-y-3`}>
        <h2 className="font-medium text-gray-900 dark:text-white">Category exclusions (optional)</h2>
        <p className="text-xs text-gray-500">Mapped to Control criteria. Screens that do not match are hidden when you pick faces.</p>
        <select
          className={inputClass}
          value=""
          onChange={(e) => {
            const v = e.target.value
            if (v && !exclusionIds.includes(v)) setExclusionIds([...exclusionIds, v])
          }}
        >
          <option value="">Add a criterion…</option>
          {criteria.map((c) => <option key={c.id} value={String(c.id)}>{c.name}</option>)}
        </select>
        <div className="flex flex-wrap gap-2">
          {exclusionIds.map((id) => (
            <button key={id} type="button" className={btnSecondary} onClick={() => setExclusionIds(exclusionIds.filter((x) => x !== id))}>
              {criteria.find((c) => String(c.id) === id)?.name || id} ×
            </button>
          ))}
        </div>
      </section>

      <section className={`${cardClass} p-6 space-y-4`}>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h2 className="font-medium text-gray-900 dark:text-white">Line items</h2>
          <div className="flex gap-2">
            <select className={inputClass} value={pkgPick} onChange={(e) => setPkgPick(e.target.value)}>
              <option value="">Select a package…</option>
              {packages.map((p) => (
                <option key={p.id} value={p.id}>{p.name}{p.published ? '' : ' (unpublished)'}</option>
              ))}
            </select>
            <button type="button" className={btnSecondary} onClick={addPackage} disabled={!pkgPick}>Add package</button>
            <button type="button" className={btnPrimary} onClick={addScreens}>Select screens</button>
          </div>
        </div>
        {!lineItems.length && <p className="text-sm text-gray-500">No line items yet. Add a package or pick screens.</p>}
        {lineItems.map((li) => (
          <div key={li.id} className="border border-gray-200 dark:border-gray-700 rounded-lg p-3 space-y-2">
            <div className="flex justify-between gap-2">
              <input className={inputClass} value={li.name} onChange={(e) => updateLine(li.id, { name: e.target.value })} />
              <button type="button" className="text-red-600 text-sm" onClick={() => setLineItems(lineItems.filter((x) => x.id !== li.id))}>Remove</button>
            </div>
            <div className="text-xs text-gray-500">
              {li.type === 'package' ? `Package · ${li.packageName}` : 'Manual screens'} · {li.displayUnitIds?.length || 0} face(s)
              {li.priceCents != null ? ` · $${(li.priceCents / 100).toFixed(2)}` : ''}
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              <input type="date" className={inputClass} value={li.flight.startDate || ''}
                onChange={(e) => updateLine(li.id, { flight: { ...li.flight, startDate: e.target.value } })} />
              <input type="date" className={inputClass} value={li.flight.endDate || ''}
                onChange={(e) => updateLine(li.id, { flight: { ...li.flight, endDate: e.target.value } })} />
              <input className={inputClass} placeholder="Ad duration (sec)" value={li.flight.adDurationSec || ''}
                onChange={(e) => updateLine(li.id, { flight: { ...li.flight, adDurationSec: e.target.value } })} />
              <input className={inputClass} placeholder="Saturation" value={li.flight.saturation || ''}
                onChange={(e) => updateLine(li.id, { flight: { ...li.flight, saturation: e.target.value } })} />
            </div>
            {li.type !== 'package' && (
              <button type="button" className={btnSecondary} onClick={() => setPickingFor(li.id)}>Edit screens</button>
            )}
          </div>
        ))}
        {picking && (
          <div className="border border-blue-200 dark:border-blue-800 rounded-lg p-3">
            <InventoryPicker
              selectedIds={picking.displayUnitIds || []}
              onChange={(ids) => updateLine(picking.id, { displayUnitIds: ids })}
              required
              title={`Faces for ${picking.name}`}
            />
            <button type="button" className={btnSecondary} onClick={() => setPickingFor(null)}>Done picking</button>
          </div>
        )}
      </section>

      <section className={`${cardClass} p-6 space-y-3`}>
        <h2 className="font-medium text-gray-900 dark:text-white">Creatives</h2>
        <input type="file" accept="image/*,video/mp4" multiple onChange={(e) => setFiles(Array.from(e.target.files || []))} />
        {files.length > 0 && <p className="text-xs text-gray-500">{files.map((f) => f.name).join(', ')}</p>}
        <textarea className={inputClass} rows={3} placeholder="Description (optional)" value={description} onChange={(e) => setDescription(e.target.value)} />
      </section>

      {error && <p className="text-sm text-red-600">{error}</p>}
      <div className="flex justify-end gap-2">
        <button type="button" className={btnSecondary} disabled={busy} onClick={() => save('draft')}>Done (draft)</button>
        <button type="button" className={btnSecondary} disabled={busy} onClick={() => save('hold')}>Hold</button>
        <button type="button" className={btnPrimary} disabled={busy} onClick={() => save('submit')}>Submit for booking</button>
      </div>
    </div>
  )
}
